import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useCreateKnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import fileService from "@/services/fileService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Globe,
  Building,
  User,
  Mic,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useCombinedAgentList } from "@/hooks/agent/useCombinedAgentList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// URL validation function
const isValidUrl = (string: string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const linkSchema = z.object({
  title: z.string().min(1, "Title is required"),
  url: z
    .string()
    .min(1, "URL is required")
    .refine((url) => isValidUrl(url), {
      message: "Please enter a valid URL (must start with http:// or https://)",
    }),
  description: z.string().min(1, "Description is required"),
  pages: z.number().min(1).max(100).default(1),
});

interface AddLinkFormProps {
  onSuccess: () => void;
  agentId?: string | null;
  initialValues?: {
    title?: string;
    url?: string;
    description?: string;
  };
}

type CrawlStage =
  | "INITIATING"
  | "CRAWLING"
  | "PROCESSING"
  | "FINALIZING"
  | "COMPLETED"
  | null;

const stageMessages: Record<
  Exclude<CrawlStage, null>,
  { message: string; progress: number }
> = {
  INITIATING: { message: "🔍 Analyzing website...", progress: 25 },
  CRAWLING: { message: "📄 Starting content extraction...", progress: 75 },
  PROCESSING: { message: "🧠 Preparing for AI search...", progress: 75 },
  FINALIZING: { message: "✅ Finalizing...", progress: 90 },
  COMPLETED: {
    message: "✅ Crawl initiated! Content will be processed in the background.",
    progress: 100,
  },
};

const AddLinkForm = ({
  onSuccess,
  agentId: initialAgentId,
  initialValues,
}: AddLinkFormProps) => {
  const { createItem, isLoading } = useCreateKnowledgeItem();
  const { organization } = useAuth();
  const { agents, isLoading: isAgentsLoading } = useCombinedAgentList();
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlStage, setCrawlStage] = useState<CrawlStage>(null);
  const [crawlError, setCrawlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedItemData, setSavedItemData] = useState<{
    id: string;
    url: string;
    title: string;
    description: string | null;
    pages: number;
  } | null>(null);

  // Agent selection state
  const [isAgentSpecific, setIsAgentSpecific] = useState(!!initialAgentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    initialAgentId || null,
  );

  const form = useForm<z.infer<typeof linkSchema>>({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      title: initialValues?.title || "",
      url: initialValues?.url || "",
      description: initialValues?.description || "",
      pages: 1,
    },
  });

  // Clear form errors when form values change
  const clearFormError = () => {
    if (formError) {
      setFormError(null);
    }
  };

  const handleAgentSpecificToggle = (checked: boolean) => {
    setIsAgentSpecific(checked);
    if (!checked) {
      setSelectedAgentId(null);
    }
  };

  const getSelectedAgentName = () => {
    if (!selectedAgentId) return null;
    const agent = agents.find((a) => a.id === selectedAgentId);
    return agent ? agent.title : "Unknown Agent";
  };

  const processLinkForAI = async (linkData: typeof savedItemData) => {
    if (!linkData || !organization) return;

    setIsCrawling(true);
    setCrawlError(null);
    setCrawlStage("INITIATING");

    try {
      setCrawlStage("CRAWLING");

      // Get the user session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firecrawl-crawl`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            url: linkData.url,
            linkId: linkData.id,
            title: linkData.title,
            description: linkData.description,
            organizationId: organization.id,
            agentId: selectedAgentId, // Use selected agent ID
            limit: linkData.pages || 1,
            scrapeOptions: {
              timeout: 30000,
              formats: ["markdown"],
              removeBase64Images: true,
              onlyMainContent: true,
              blockAds: true,
              waitFor: 2000,
              mobile: false,
            },
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to initiate crawl: ${response.status} ${errorText}`,
        );
      }

      const result = await response.json();

      if (result.id) {
        // Crawl initiated successfully - update database with crawl_id
        const { error: updateError } = await supabase
          .from("knowledge_items")
          .update({
            crawl_id: result.id,
            crawl_status: "crawling",
            updated_at: new Date().toISOString(),
          })
          .eq("id", linkData.id);

        if (updateError) {
          console.error("Failed to update crawl status:", updateError);
          throw new Error("Failed to update crawl status");
        }

        console.log(`Crawl initiated successfully with ID: ${result.id}`);

        // Show completion message - the webhook will handle the actual content processing
        setCrawlStage("COMPLETED");

        // Success - clear form and close modal after showing completion
        setTimeout(() => {
          form.reset();
          setSavedItemData(null);
          setCrawlStage(null);
          onSuccess();
        }, 2000);
      } else {
        throw new Error("No crawl ID returned from Firecrawl");
      }
    } catch (error) {
      console.error("Error processing link for AI:", error);
      setCrawlError(
        error instanceof Error ? error.message : "Failed to initiate crawl",
      );
    } finally {
      setIsCrawling(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof linkSchema>) => {
    try {
      setFormError(null); // Clear any previous form errors

      // Determine agent type
      const selectedAgent = selectedAgentId ? agents.find(a => a.id === selectedAgentId) : null;
      const isVoiceAgent = selectedAgent?.type === "voice";

      // First, save the link to the database with agent_id or voice_agent_id if provided
      const result = await createItem({
        title: values.title,
        url: values.url,
        description: values.description || null,
        type: "link",
        ...(selectedAgentId ? (isVoiceAgent 
          ? { voice_agent_id: selectedAgentId }
          : { agent_id: selectedAgentId }
        ) : {}),
      });

      if (result) {
        // Store the saved item data for potential retry
        const itemData = {
          id: result.id,
          url: values.url,
          title: values.title,
          description: values.description || null,
          pages: values.pages,
        };
        setSavedItemData(itemData);

        // Automatically start crawling the link
        await processLinkForAI(itemData);
      } else {
        // Handle the case where createItem returns null
        setFormError(
          "Failed to save the link. Please check your permissions and try again.",
        );
        console.error(
          "createItem returned null - possible permission or validation issue",
        );
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      setFormError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while saving the link.",
      );
    }
  };

  const handleRetry = () => {
    if (savedItemData) {
      processLinkForAI(savedItemData);
    }
  };

  const currentStage =
    crawlStage && crawlStage !== "COMPLETED" ? stageMessages[crawlStage] : null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Agent Selection Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="agent-specific" className="text-sm font-medium">
                Upload for specific agent
              </Label>
              <p className="text-xs text-muted-foreground">
                Choose whether this website should be available to all agents or
                just one specific agent
              </p>
            </div>
            <Switch
              id="agent-specific"
              checked={isAgentSpecific}
              onCheckedChange={handleAgentSpecificToggle}
              disabled={isAgentsLoading || isLoading || isCrawling}
            />
          </div>

          {isAgentSpecific ? (
            <div className="space-y-2">
              <Label className="text-sm">Select Agent</Label>
              <Select
                value={selectedAgentId || ""}
                onValueChange={setSelectedAgentId}
                disabled={isAgentsLoading || isLoading || isCrawling}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center">
                        {agent.type === "voice" ? (
                          <Mic className="mr-2 h-4 w-4 text-blue-600" />
                        ) : (
                        <User className="mr-2 h-4 w-4" />
                        )}
                        {agent.title}
                        {agent.type === "voice" && (
                          <span className="ml-2 text-xs text-muted-foreground">(Voice)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAgentId && (
                <Alert className="mt-2">
                  <User className="h-4 w-4" />
                  <AlertTitle>Agent-Specific Website</AlertTitle>
                  <AlertDescription>
                    This website will be crawled for{" "}
                    <strong>{getSelectedAgentName()}</strong> and will only be
                    accessible to that agent.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <Alert>
              <Building className="h-4 w-4" />
              <AlertTitle>Organization Website</AlertTitle>
              <AlertDescription>
                This website will be crawled for the organization and will be
                accessible to all agents.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title *</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter a title for this link"
                  {...field}
                  disabled={isLoading || isCrawling}
                  onChange={(e) => {
                    field.onChange(e);
                    clearFormError();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL *</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://example.com"
                  {...field}
                  disabled={isLoading || isCrawling}
                  onChange={(e) => {
                    field.onChange(e);
                    clearFormError();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description *</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe what this link contains and why it's useful"
                  className="resize-none h-24"
                  {...field}
                  value={field.value || ""}
                  disabled={isLoading || isCrawling}
                  onChange={(e) => {
                    field.onChange(e);
                    clearFormError();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="pages"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pages to Crawl: {field.value}</FormLabel>
              <FormControl>
                <div className="px-2">
                  <Slider
                    min={1}
                    max={100}
                    step={1}
                    value={[field.value]}
                    onValueChange={(value) => field.onChange(value[0])}
                    disabled={isLoading || isCrawling}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>1 page</span>
                    <span>100 pages</span>
                  </div>
                </div>
              </FormControl>
              <p className="text-xs text-muted-foreground">
                More pages = more content but longer crawling time. Most
                websites need 1-10 pages.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Crawling Progress */}
        {isCrawling && currentStage && (
          <div className="space-y-3 rounded-lg border p-4 bg-muted/50">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 animate-pulse text-primary" />
              <span className="text-sm font-medium">
                {currentStage.message}
              </span>
            </div>
            <Progress value={currentStage.progress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              Crawling {form.getValues("pages")} page(s). This usually takes
              30-90 seconds per page.
            </p>
          </div>
        )}

        {/* Success Message */}
        {crawlStage === "COMPLETED" && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">Success!</AlertTitle>
            <AlertDescription className="text-green-700">
              Website content has been crawled and indexed for AI search.
            </AlertDescription>
          </Alert>
        )}

        {/* Error Message */}
        {crawlError && !isCrawling && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to crawl website</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{crawlError}</p>
              {savedItemData && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="mt-2"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry Crawl
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Form Error */}
        {formError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{formError}</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            disabled={
              isLoading || isCrawling || (isAgentSpecific && !selectedAgentId)
            }
          >
            {isLoading ? (
              <>
                <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </>
            ) : isCrawling ? (
              <>
                <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Crawling...
              </>
            ) : (
              "Crawl Website"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddLinkForm;
